import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'

import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import EnrolmentsTable from './EnrolmentsTable.js'
import ErrorDisplay from './ErrorDisplay';
import Waiting from './Waiting';
import Invoice from './Invoice';
import PayPalButton from './PayPalButton';
import InvoicePdf from './InvoicePdf';
import EnrolmentsPdf from './EnrolmentsPdf';
import CourseDetail from './CourseDetail';
import EnrolmentWithdrawalDialog from './EnrolmentWithdrawalDialog';

import PageTitle from './PageTitle';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import useMediaQuery from '@mui/material/useMediaQuery';
import clsx from 'clsx';
import useStyles from '../styles/Styles';

import _ from 'lodash';
import dayjs from 'dayjs';

import { loadSettings, loadEnrolmentsSettings, fetchEnrolments, resetErrorMessage } from '../actions';

import { describeDate } from '../utils';

const Enrolments = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const isSm = useMediaQuery('(min-width:650px)');
	const isMd = useMediaQuery('(min-width:960px)');
	const flexLayout = useMediaQuery('(max-width:850px)');

	const fetched = useSelector(state => state.enrolments.expires);
	const errorMsg = useSelector(state => state.errorMessage);
	const submitting = useSelector(state => state.enrolments.submitting);
	const enrolmentsSettings = useSelector(state => state.settings.enrolments.data);
	const enrolments = useSelector(state => state.enrolments.data, _.isEqual );
	const generalSettings = useSelector(state => state.settings.general.data);
	const invoiceSettings = useSelector(state => state.settings.invoices.data);
	const memberLabels = useSelector(state => state.settings.labels_members.data);
	const courseLabels = useSelector(state => state.settings.labels_courses.data);
	const user = useSelector(state => state.user);

	useEffect(() => {
		// This also dispatches enrolments request if required
		dispatch(loadEnrolmentsSettings(true));
		dispatch(loadSettings('invoices'));
	}, [dispatch])
	
	const [displayRangeDesc, setDisplayRangeDesc] = useState('Current timetable');
	const [timetableDesc, setTimetableDesc] = useState('Current timetable');
	
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);
	
	useEffect(() => {
		if(fromDate !== null && toDate !== null){
			dispatch(fetchEnrolments(fromDate, toDate));
		}
	}, [dispatch, fromDate, toDate])
	
	useEffect(() => {
		if(enrolmentsSettings.member_courses_start_date){
			setFromDate(enrolmentsSettings.member_courses_start_date);
		}
		
		if(enrolmentsSettings.member_courses_end_date){
			setToDate(enrolmentsSettings.member_courses_end_date);
		}
		
		setTimetableDesc('Current timetable (' + describeDate(enrolmentsSettings.member_courses_start_date) + ' - ' + describeDate(enrolmentsSettings.member_courses_end_date) + ')');
	}, [enrolmentsSettings])
	
	const perPage = 10;
	const [ePage, setEPage] = useState(0);
	const [ePages, setEPages] = useState(0);
	
	const [sPage, setSPage] = useState(0);
	const [sPages, setSPages] = useState(0);
	
	const [wPage, setWPage] = useState(0);
	const [wPages, setWPages] = useState(0);
	
	const [vPage, setVPage] = useState(0);
	const [vPages, setVPages] = useState(0);
	
	const [filteredEnrolments, setFilteredEnrolments] = useState(enrolments.enrolled);
	const [filteredSubs, setFilteredSubs] = useState(enrolments.subscriptions);
	
	useEffect(() => {

		const FE = _.chain(enrolments.enrolled).filter(e => {
			return e.finish_date >= fromDate && e.start_date <= toDate
		}).orderBy('start_date', 'start_time').value();

		const FS = _.chain(enrolments.subscriptions).filter(e => {
			return e.finish_date >= fromDate && e.start_date <= toDate
		}).orderBy('start_date').value();
		
		setFilteredEnrolments(FE);
		setEPage(1);
		setEPages(Math.ceil(FE.length / perPage));
		
		setFilteredSubs(FS);
		setSPage(1);
		setSPages(Math.ceil(FS.length / perPage));
		
		setDisplayRangeDesc( describeDate(fromDate) + ' - ' + describeDate(toDate) );
		
	}, [enrolments, fromDate, toDate])
	
	const [filteredWaits, setFilteredWaits] = useState(enrolments.waitlisted);
	useEffect(() => {
		const FW = _.chain(enrolments.waitlisted).filter(e => {
			return e.finish_date >= enrolmentsSettings.member_courses_start_date
			&& e.start_date <= enrolmentsSettings.member_courses_end_date
		}).orderBy('start_date', 'start_time').value();
	
		setFilteredWaits(FW);
		setWPage(1);
		setWPages(Math.ceil(FW.length / perPage));
		
	}, [enrolments, enrolmentsSettings])
	
	// No need to filter special interests - we always display them all 
	const [interestsArray, setInterestsArray] = useState(enrolments.special_interests);
	useEffect(() => {
		const IA = _.values(enrolments.special_interests);
		setInterestsArray(IA);
		setVPage(1);
		setVPages(Math.ceil(IA.length / perPage));
	}, [enrolments])
	
	const [showingEnrolments, setShowingEnrolments] = useState([]);
	useEffect(() => {
		setShowingEnrolments( _.slice(filteredEnrolments, perPage * (ePage-1), perPage*ePage) );
	}, [ePage, filteredEnrolments])
	
	const [showingSubs, setShowingSubs] = useState([]);
	useEffect(() => {
		setShowingSubs( _.slice(filteredSubs, perPage * (sPage-1), perPage*sPage) );
	}, [sPage, filteredSubs])
	
	const [showingWaits, setShowingWaits] = useState([]);
	useEffect(() => {
		setShowingWaits( _.slice(filteredWaits, perPage * (wPage-1), perPage*wPage) );
	}, [wPage, filteredWaits])
	
	const [showingSpecials, setShowingSpecials] = useState([]);
	useEffect(() => {
		setShowingSpecials( _.slice( interestsArray, perPage * (vPage-1), perPage*vPage) );
	}, [vPage, interestsArray])
	
	const subsFields = {
		course_code: "Code",
		course_name: "Name",
		start_date: "Start date",
		finish_date: 'End date',
		amount_charged: "Fee",
		invoice_no: "Invoice",
	}
	
	const courseFields = {
		//course_code: "Code",
		//course_name: "Name",
		course: courseLabels.courseCapLabel,
		start: "Start",
		//start_date: "Start date",
		//start_time: "Start time",
		finish_date: 'End date',
		//venue_code: courseLabels.venue,
		detail: "Actions"
	}
	
	const waitFields = {
		course: courseLabels.courseCapLabel,
		start: "Start",
		finish_date: 'End date',
		//venue_code: courseLabels.venue,
		detail: "Actions"
	}
	
	const specialFields = {
		interest_code: "Code",
		short_comment: "Note",
		points: "Points"
	}
	
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [showingInvoice, setShowingInvoice] = useState(false);
	const [invoiceUnpaid, setInvoiceUnpaid] = useState(false);
	const [showingCourse, setShowingCourse] = useState(false);
	const [removalRequestOpen, setRemovalRequestOpen] = useState(false);
	const [undoingRemoval, setUndoingRemoval] = useState(false);
		
	const InvoiceDialog = () => {
		return (
		<Dialog
			open={showingInvoice !== false}
			onClose={() => {
				setShowingInvoice(false);
				setInvoiceUnpaid(false);
			}}
			fullScreen={!isSm}
			className={classes.settingsDialog}
		>
		<DialogTitle>Invoice Detail</DialogTitle>
		<DialogContent dividers className={classes.invoiceDetail}>
		{showingInvoice ? 
		<Invoice 
			invoiceId={showingInvoice}
			isSm={isSm} 
			isMd={isMd} 
			flexLayout={flexLayout}
		/> : null }
		</DialogContent>
        <DialogActions>
			<Button color="primary" size={isSm ? 'medium' : 'small'}
				onClick={() => {
					setShowingInvoice(false);
					setInvoiceUnpaid(false);
				}}
			>
			Close
			</Button>
			<InvoicePdf 
				buttonSize={isSm ? 'medium' : 'small'}
				invoiceId={showingInvoice}
			/>
			{invoiceSettings.paypal.accept && invoiceUnpaid ?
			<PayPalButton 
				invoiceNo={showingInvoice}
			/>
			: null}
        </DialogActions>
		</Dialog>
	)}
	
	const CourseDialog = () => {
		return (
		<Dialog
			open={showingCourse !== false}
			onClose={() => {
				setShowingCourse(false);
				setInvoiceUnpaid(false);
				dispatch(resetErrorMessage());
			}}
			fullScreen={!isSm}
			className={classes.settingsDialog}
		>
		<DialogTitle>
		<Typography variant="subtitle2" component="span" display="block" >{courseLabels.courseCapLabel} detail</Typography>
		<Typography variant="h6" component="span">{showingCourse.course_name}</Typography>
		</DialogTitle>
		<DialogContent dividers className={classes.invoiceDetail}>
		{showingCourse ? 
		<CourseDetail 
			courseCode={showingCourse.course_code}
			loggedIn={user.member_no > 0}
			isSm={isSm}
		/> : null }
		</DialogContent>
        <DialogActions>
			<Button color="primary" size={isSm ? 'medium' : 'small'}
				onClick={() => {
					setShowingCourse(false);
					setInvoiceUnpaid(false);
					dispatch(resetErrorMessage());
				}}
			>
			Close
			</Button>
			{invoiceUnpaid ? 
			<Button color="primary" variant="contained" size={isSm ? 'medium' : 'small'}
				onClick={() => {
					setShowingCourse(false);
					setShowingInvoice(showingCourse.invoice_no)
				}}
			>
			View invoice
			</Button>
			: null}
        </DialogActions>
		</Dialog>
	)}
	
	const DatesDescription = () => (
	<Typography paragraph>
		{displayRangeDesc}
		<IconButton aria-label="settings" color="primary" size='small' 
				onClick={() => {setSettingsOpen(true)}}
			>
		<EditOutlinedIcon />
		</IconButton>
	</Typography>
	);
	
	const DisplaySettings = () => {
		
		const [data, setData] = useState({
			from: _.cloneDeep(fromDate),
			to: _.cloneDeep(toDate)
		})
		
		const [errors, setErrors] = useState({
			from: false,
			to: false
		})
		
		const handleChange = (date, key) => {
			setData({...data, [key]: date})
			
			if(dayjs(date).isValid()){
				setErrors({...errors, [key]: false})
			} else {
				setErrors({...errors, [key]: true})
			}
		}
		
		const datePickerProps = {
			closeOnSelect: true,
			className: classes.datePicker,
			InputAdornmentProps: { position: "end"},
			minDate: dayjs().startOf('year').subtract(10, 'years'),
			maxDate: dayjs().endOf('year').add(10, 'years'),
			inputFormat: "DD/MM/YYYY"
		}
		
		const datePickerTextFieldProps = {
			required: true,
			fullWidth: true
		}
		
		return (
		<>
		<Dialog
		open={settingsOpen}
		onClose={() => setSettingsOpen(false)}
		fullScreen={!isSm}
		aria-labelledby="setings-dialog-title"
		className={classes.settingsDialog}
		>
		<DialogTitle id="settting-dialog-title">Enrolments Display Settings</DialogTitle>
		<DialogContent dividers>
		
		<Grid container direction="column" spacing={2}>
		<Grid item>
		<Typography paragraph>These settings control the display of <strong>Membership Subscriptions</strong> and <strong>Enrolments</strong>: All subscriptions or enrolments that are running within the selected date range are displayed. <strong>Waitlist</strong> enrolments are displayed for the current timetable only. {generalSettings.special_interests_on ?
		<>All <strong>{memberLabels.volunteers}</strong> items are displayed.</>
		: null}
		</Typography>
		</Grid>
		<Grid item>
		<DatePicker {...datePickerProps}
			value={data.from}
			onChange={(date) => {handleChange(date, 'from')}}
			label="From date"
			renderInput={(props) => <TextField {...props} {...datePickerTextFieldProps}
				error={errors.from}
				helperText={errors.from ? 'Please enter a date in the format DD/MM/YYYY' : ''}
				placeholder="Display enrolments from"
			/>}
		/>
		</Grid>
		<Grid item>
		<DatePicker {...datePickerProps}
			value={data.to}
			onChange={(date) => {handleChange(date, 'to')}}
			label="To date"
			renderInput={(props) => <TextField {...props} {...datePickerTextFieldProps}
				error={errors.to}
				helperText={errors.to ? 'Please enter a date in the format DD/MM/YYYY' : ''}
				placeholder="Display enrolments to"
			/>}
		/>
		</Grid>
		</Grid>
		
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} color="primary">
            Cancel
          </Button>
          <Button color="primary" autoFocus
			disabled={errors.from || errors.to}
			onClick={() => {
				setFromDate(dayjs(data.from).format('YYYY-MM-DD'));
				setToDate(dayjs(data.to).format('YYYY-MM-DD'));
				setSettingsOpen(false);
			}} 
		>
            Apply Changes
          </Button>
        </DialogActions>
		</Dialog>
		</>
	)}
	
	const SettingsBar = () => (
		<Paper className={clsx(classes.innerContent, classes.paginator)} square={true} >
		<Grid container spacing={isMd ? 2 : 1} 
			alignItems="center"
			justifyContent={isMd ? 'flex-end' : 'center'} 
		>
		<Grid item>

		<EnrolmentsPdf
			subscriptions={filteredSubs}
			enrolments={filteredEnrolments}
			waitlists={filteredWaits}
			interests={generalSettings.special_interests_on ? interestsArray : false}
			displayRange={displayRangeDesc}
			timetableRange={timetableDesc}
			interestsName={memberLabels.volunteers}
			isSm={isSm}
		/>
		
		</Grid>
		<Grid item>
		
		<Button color="primary" 
			onClick={() => {setSettingsOpen(true)}}
		>Settings</Button>

		</Grid>
		</Grid>
		</Paper>
	);
	
	return (
	<>
	<ErrorDisplay />
	<PageTitle title='My Enrolments' withoutPaper  />
	{ (fetched === 0 && !errorMsg) || submitting ? 
		<Waiting 
			message='Fetching enrolments. Please wait...'
			withPaper
		/> :
	<>
	<DisplaySettings/>
	<InvoiceDialog/>
	<CourseDialog/>
	<EnrolmentWithdrawalDialog
		course={removalRequestOpen}
		courseLabels={courseLabels}
		setRemovalRequestOpen={setRemovalRequestOpen}
		undoingRemoval={undoingRemoval}
	/>
	<SettingsBar/>
	
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<Typography variant="h5" component="h2" paragraph>Membership Subscriptions</Typography>
	<DatesDescription/>
	{fetched && showingSubs.length === 0 ? 
	<Typography paragraph>No subscriptions between {describeDate(fromDate)} and {describeDate(toDate)}. Try a different date range?</Typography> 
	: null }
	<EnrolmentsTable
		items={showingSubs}
		itemsLabel={'Subscriptions'}
		itemLabel={'Subscription'}
		totalLength={filteredSubs.length}
		fields={subsFields}
		page={sPage}
		pages={sPages}
		perPage={perPage}
		setPage={setSPage}
		size={isMd ? "medium" : "small"}
		flexLayout={!isMd}
		openItem={setShowingInvoice}
		setInvoiceUnpaid={setInvoiceUnpaid}
	/>
	</Paper>
	
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<Typography variant="h5" component="h2" paragraph>Enrolments</Typography>
	<DatesDescription/>
	{fetched && showingEnrolments.length === 0 ? 
	<Typography paragraph>No enrolments between {describeDate(fromDate)} and {describeDate(toDate)}. Try a different date range?</Typography> 
	: null }
	<EnrolmentsTable
		items={showingEnrolments}
		itemsLabel={'Enrolments'}
		itemLabel={courseLabels.courseLabel}
		totalLength={filteredEnrolments.length}
		fields={courseFields}
		page={ePage}
		pages={ePages}
		perPage={perPage}
		setPage={setEPage}
		size={isMd ? "medium" : "small"}
		flexLayout={!isMd}
		openItem={setShowingCourse}
		openRemoveItem={setRemovalRequestOpen}
		setUndoingRemoval={setUndoingRemoval}
		setInvoiceUnpaid={setInvoiceUnpaid}
	/>
	</Paper>
	
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<Typography variant="h5" component="h2" paragraph>Waitlist enrolments</Typography>
	<Typography paragraph>{timetableDesc}</Typography>
	{fetched && showingWaits.length === 0 ? 
	<Typography paragraph>You have no waitlist enrolments for the current timetable.</Typography> 
	: null }
	<EnrolmentsTable
		items={showingWaits}
		itemsLabel={'Waitlist enrolments'}
		itemLabel={courseLabels.courseLabel}
		totalLength={filteredWaits.length}
		fields={waitFields}
		page={wPage}
		pages={wPages}
		perPage={perPage}
		setPage={setWPage}
		size={isMd ? "medium" : "small"}
		flexLayout={!isMd}
		openItem={setShowingCourse}
		openRemoveItem={setRemovalRequestOpen}
		setUndoingRemoval={setUndoingRemoval}
		setInvoiceUnpaid={setInvoiceUnpaid}
	/>
	</Paper>
	
	{generalSettings.special_interests_on ?
	<Paper className={clsx(classes.innerContent, classes.page)} square={true}>
	<Typography variant="h5" component="h2" paragraph>{memberLabels.volunteers}</Typography>
	
	{fetched && showingSpecials.length === 0 ? 
	<Typography paragraph>No items found.</Typography> 
	: 
	<EnrolmentsTable
		items={interestsArray}
		itemsLabel={'Items'}
		itemLabel={'Item'}
		totalLength={interestsArray.length}
		fields={specialFields}
		page={vPage}
		pages={vPages}
		perPage={perPage}
		setPage={setVPage}
		size={isMd ? "medium" : "small"}
		flexLayout={!isMd}
	/>
	}
	</Paper>
	: null }
	
	<SettingsBar />
	
	</> }
	</>
)};

export default Enrolments